import {useEffect} from "react";

// react-router-dom components
import {useLocation, NavLink as RouterNavLink} from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";


import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useTheme} from "@mui/material/styles";

// Soft UI Dashboard React components
import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";

// Soft UI Dashboard React examples
import SidenavCollapse from "./SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "./SidenavRoot";

// Soft UI Dashboard React context
import {setMiniSidenav, useRayzayUIController} from "../../context";

import {filterRoutesByRole, getUserRole} from "../../utils/roleUtils";

const floatStyle = `
  @keyframes floatY {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
  .hero-float {
    animation: floatY 2s ease-in-out infinite;
  }
`;

function Sidenav({color = "info", brand = "", brandName, routes, ...rest}) {
  const [controller, dispatch] = useRayzayUIController();
    const {miniSidenav, transparentSidenav, loggedInUserDetail} = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const theme = useTheme();
  // Desktop = xl breakpoint (1200+); below that we use a temporary drawer.
  const isDesktop = useMediaQuery(theme.breakpoints.up("xl"));

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  // Auto-close the temporary drawer whenever the route changes on mobile.
  useEffect(() => {
    if (!isDesktop) {
      setMiniSidenav(dispatch, true);
    }
  }, [pathname, isDesktop, dispatch]);

  // Sync miniSidenav on viewport resize: collapsed below xl, expanded above.
  useEffect(() => {
    setMiniSidenav(dispatch, !isDesktop);
  }, [isDesktop, dispatch]);

    // Get filtered routes based on user role
    const userRole = getUserRole(loggedInUserDetail);
    const filteredRoutes = filterRoutesByRole(routes, userRole);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
    const renderRoutes = filteredRoutes.map(({type, name, icon, title, noCollapse, key, route, href}) => {
    let returnValue;

    if (type === "collapse") {
      returnValue = href ? (
        <SidenavCollapse
          color={color}
          name={name}
          icon={icon}
          active={key === collapseName}
          noCollapse={noCollapse}
          href={href}
        />
      ) : (
        <SidenavCollapse
          color={color}
          key={key}
          name={name}
          icon={icon}
          active={key === collapseName}
          noCollapse={noCollapse}
          route={route}
        />
      );
    } else if (type === "title") {
      returnValue = (
        <SoftTypography
          key={key}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          opacity={0.6}
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </SoftTypography>
      );
    } else if (type === "divider") {
      returnValue = <Divider key={key} />;
    }

    return returnValue;
  });

  return (
    <SidenavRoot
      {...rest}
      variant={isDesktop ? "permanent" : "temporary"}
      open={isDesktop || !miniSidenav}
      onClose={closeSidenav}
      ModalProps={{keepMounted: true}}
      ownerState={{transparentSidenav, miniSidenav}}
    >
      <SoftBox pt={3} pb={2} px={2} textAlign="center">
        <SoftBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <SoftTypography variant="h6" color="text">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </SoftTypography>
        </SoftBox>
        <RouterNavLink to="/dashboard" display="column" alignItems="center">
          {brand && (
            <SoftBox
              component="img"
              src={brand}
              alt="Mytrustie"
              className="hero-float"
              sx={{ width: "100%", maxWidth: "13rem", display: "block" }}
            />
          )}
          {/*<SoftBox*/}
          {/*  width={!brandName && "100%"}*/}
          {/*  sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}*/}
          {/*>*/}
          {/*  <SoftTypography*/}
          {/*    component="h6" variant="button" fontWeight="medium">*/}
          {/*    {brandName}*/}
          {/*  </SoftTypography>*/}
          {/*</SoftBox>*/}
        </RouterNavLink>
      </SoftBox>
      <style>{floatStyle}</style>
      <Divider />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}


// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
